import React, { useEffect, useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { extractDiscountInfo } from './utils';

export default function ReservationCalendarView({
  reservations = [],
  isLoading,
  onDateSelect,
  currentMonth,
  capacity = 100,
}) {
  const [dateCursor, setDateCursor] = useState(currentMonth || new Date());

  useEffect(() => {
    if (currentMonth && !isSameMonth(currentMonth, dateCursor)) {
      setDateCursor(currentMonth);
    }
  }, [currentMonth, dateCursor]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(dateCursor);
    const monthEnd = endOfMonth(monthStart);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [dateCursor]);

  const getDayReservations = (day) => reservations.filter((reservation) => {
    if (!reservation?.reservation_date) return false;
    return isSameDay(parseISO(String(reservation.reservation_date)), day);
  });

  const getOccupancyColor = (covers, max) => {
    const percentage = max > 0 ? (covers / max) * 100 : 0;
    if (percentage >= 90) return 'bg-red-100 text-red-700 border-red-200';
    if (percentage >= 70) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  if (isLoading && !reservations.length) {
    return (
      <div className="flex h-full items-center justify-center rounded-md border bg-background">
        <span className="text-sm text-slate-500">Kalender wird geladen…</span>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex flex-col items-center justify-between gap-2 rounded-lg border bg-card p-1.5 shadow-sm sm:flex-row">
        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex items-center rounded-md border bg-background shadow-sm">
            <Button variant="ghost" size="icon" onClick={() => setDateCursor(subMonths(dateCursor, 1))} className="h-7 w-7 rounded-r-none">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { const today = new Date(); setDateCursor(today); onDateSelect?.(today); }} className="h-7 rounded-none border-x px-2 text-xs font-medium">
              Heute
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setDateCursor(addMonths(dateCursor, 1))} className="h-7 w-7 rounded-l-none">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          <h2 className="min-w-[140px] text-center text-sm font-semibold">
            {format(dateCursor, 'MMMM yyyy')}
          </h2>
        </div>

        <div className="flex items-center gap-1 rounded-lg bg-muted/10 p-0.5">
          <Button variant="default" size="sm" className="h-6 px-2 text-[10px]">
            <CalendarIcon className="mr-1 h-3.5 w-3.5" /> Monat
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden rounded-md border bg-white shadow-sm">
        <div className="grid grid-cols-7 border-b bg-muted/10">
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((weekday) => (
            <div key={weekday} className="py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {weekday}
            </div>
          ))}
        </div>

        <div className="grid h-full auto-rows-fr grid-cols-7 gap-px overflow-hidden bg-muted/20">
          {calendarDays.map((day) => {
            const dayReservations = getDayReservations(day);
            const isCurrentMonth = isSameMonth(day, dateCursor);
            const isDayToday = isToday(day);
            const totalGuests = dayReservations.reduce((sum, reservation) => sum + Number(reservation.party_size || 0), 0);
            const occupancyColor = getOccupancyColor(totalGuests, capacity);

            return (
              <button
                key={day.toISOString()}
                type="button"
                className={cn(
                  'group relative flex min-h-0 flex-col gap-1 bg-background p-1 text-left transition-all hover:bg-accent/5',
                  !isCurrentMonth && 'bg-muted/5 text-muted-foreground',
                  isDayToday && 'z-10 ring-1 ring-inset ring-primary',
                )}
                onClick={() => onDateSelect?.(day)}
              >
                <div className="flex items-start justify-between shrink-0">
                  <span
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium transition-colors',
                      isDayToday ? 'bg-primary text-primary-foreground' : 'text-foreground/80 group-hover:bg-muted',
                    )}
                  >
                    {format(day, 'd')}
                  </span>
                  {dayReservations.length ? (
                    <div className={cn('flex items-center gap-0.5 rounded-full border px-1 py-0 text-[9px] font-medium', occupancyColor)}>
                      <Users className="h-2.5 w-2.5" />
                      <span>{Math.round((totalGuests / capacity) * 100)}%</span>
                    </div>
                  ) : null}
                </div>

                <div className="mt-0.5 flex flex-1 flex-col gap-0.5 overflow-hidden">
                  {dayReservations.slice(0, 4).map((reservation) => {
                    const discountInfo = extractDiscountInfo(reservation);
                    const tone = String(reservation.status || '') === 'confirmed'
                      ? 'bg-blue-50 border-blue-100 text-blue-700'
                      : String(reservation.status || '') === 'seated'
                        ? 'bg-green-50 border-green-100 text-green-700'
                        : String(reservation.status || '') === 'pending'
                          ? 'bg-yellow-50 border-yellow-100 text-yellow-700'
                          : 'bg-gray-50 border-gray-100 text-gray-600';

                    return (
                      <div key={reservation.id} className={cn('relative truncate rounded border px-1 py-0 text-[9px]', tone)}>
                        <span className="truncate font-medium">{reservation.guest_name}</span>
                        {discountInfo.hasDiscount ? (
                          <div className="absolute bottom-0 right-0 top-0 flex items-center justify-center bg-black px-0.5 text-[7px] font-bold text-white">
                            %
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {dayReservations.length > 4 ? (
                    <div className="rounded bg-muted/10 text-center text-[9px] font-medium text-muted-foreground">
                      + {dayReservations.length - 4}
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
